import * as React from "react";
import classNames from "classnames";
import "./index.scss";
import { UploadFile, UploadProgress } from "../../types/files";
import utilsDocs from "../../shared/utils/docs";
import {
  Box,
  Button,
  getColor,
  Icons,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import { useEffect, useState } from "react";
import useDocumentStore from "../../redux/dispatcher/useDocumentStore";
import { t } from "i18next";
import { nanoid } from "@reduxjs/toolkit";
import { LoginDto } from "../../types/auth";
import { getUserInfoFromLocalStorage } from "../../shared/utils/auth";

interface IProps {
  isExpanded?: boolean;
  position?: "bottom";
  progressItems?: UploadProgress;
}

const ProgressBar: React.FC<IProps> = ({
  isExpanded, // 초기 프로그래스바 확정 여부
  position = "", // 프로그래스바 위치. 근데 bottom 밖에 안쓰이는듯
  progressItems, // uploadDocProgress와 동일
  ...props
}) => {
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);

  /** Hook 정리 */
  const toast = useToast();
  const { setUpdateUploadProgressFiles, recallGetContentList } =
    useDocumentStore();

  /** State 정리 */
  const [isExpandedView, setIsExpandedView] = useState(isExpanded); // 프로그래스바 확장 여부

  /** UseEffect정리 */
  useEffect(() => {
    const progressBar = document.getElementById("progressBar");
    if (progressItems?.uploadFiles && progressItems?.uploadFiles?.length > 0) {
      if (progressBar) {
        progressBar.style.width = progressWidth();
      }
    }
  }, [progressItems?.uploadFiles]);
  useEffect(() => {
    if (!progressItems) return;
    if (
      progressItems?.totalCount ===
      progressItems?.successCount + progressItems?.failCount
    ) {
      if (progressItems.failCount > 0)
        toast.toastMsg(
          nanoid(),
          t("file.progress.fail", { num: progressItems.failCount }),
          "error"
        );
      else if (progressItems?.uploadFiles[0]?.fileType !== "DROPBOX") {
        toast.toastMsg(nanoid(), t("file.progress.finishInfo"), "success");
      }
      if (progressItems?.successCount > 0) {
        recallGetContentList();
      }
    }
    if (progressItems.totalCount === progressItems.successCount) {
      const timer = setTimeout(
        () => setUpdateUploadProgressFiles({} as UploadFile),
        10000
      );
      return () => clearTimeout(timer);
    }
  }, [
    progressItems?.totalCount,
    progressItems?.successCount,
    progressItems?.failCount,
  ]);

  /** 함수 정리 */
  /** UI: 프로그래스바에서 업로드상태를 표시 */
  const showStatusMsg = () => {
    return !progressItems ||
      progressItems?.totalCount === progressItems?.successCount
      ? t("file.progress.finish")
      : progressItems?.totalCount >
        progressItems?.successCount + progressItems?.failCount
      ? t("file.progress.ongoing")
      : t("file.progress.fail", { num: progressItems?.failCount });
  };
  /** UI : 프로그래스바의 문서 한개씩의 상태 */
  const renderItems = (): React.ReactNode => {
    return progressItems?.uploadFiles?.map((item, index) => {
      const isSupportExt = [
        ...new Set([
          ...(loginDto?.serviceConfig?.revisionExtensions || []),
          ...(loginDto?.serviceConfig?.manualRevisionExtensions || []),
        ]),
      ].includes(item.fileName?.split(".")?.pop() ?? "");

      return (
        <div key={index} className={"progress-bar__item"}>
          <Box alignItems="center" width={"calc(100% - 255px)"}>
            {utilsDocs.renderDocIcon(item.fileName, "32px")}
            <Tooltip maxWidth="calc(100% - 50px)" message={item.fileName}>
              <Typography type="body2_rg" ellipsis classes="mg ml-10">
                {item.fileName}
              </Typography>
            </Tooltip>
          </Box>
          <Box width={240} alignItems="center">
            <Typography type="body2_rg" width={120}>
              {utilsDocs.fileSizeFormat(item.fileSize)}
            </Typography>
            <Box
              classes="mg ml-auto"
              alignItems="center"
              justifyContent="flex-end"
              width={120}
            >
              <Tooltip
                message={
                  item.errorMsg
                    ? item.errorMsg
                    : t("file.alert.notSupportedExt")
                }
                hideToolTip={item.errorMsg === "" && isSupportExt}
                classes="mg mr-4"
              >
                {item.status === "FAIL" && (
                  <Typography
                    type="body2_underline"
                    exactColor={getColor("COLOR_TEXT_ERROR")}
                  >
                    {t("file.progress.error")}
                  </Typography>
                )}
              </Tooltip>
              <Tooltip
                message={t("file.noWrappingFileInclude")}
                hideToolTip={!(item?.status === "SUCCESS" && !isSupportExt)}
                classes="mg mr-4"
              >
                {utilsDocs.renderDocProgressIcon(
                  item?.status ?? "FAIL",
                  isSupportExt
                )}
              </Tooltip>
            </Box>
          </Box>
        </div>
      );
    });
  };
  /** UI: 프로그래스바 진행률 */
  const drawProgressValue = (): string => {
    const progressValue =
      !progressItems ||
      progressItems?.totalCount ===
        progressItems?.successCount + progressItems?.failCount
        ? 100
        : ((progressItems?.successCount + progressItems?.failCount) /
            progressItems?.totalCount) *
          100;
    return progressValue + "%";
  };
  /**  UI: 프로그래스바의 너비 */
  const progressWidth = (): string => {
    const sideMenuChild2 = document.getElementById("sideMenuChild2");

    const sideMenuWidth =
      sideMenuChild2 && sideMenuChild2?.offsetWidth
        ? sideMenuChild2?.offsetWidth + "px"
        : "236px";
    const workspaceWidth = window.eco.serviceType === "OCI" ? "48px" : "0px";
    return `calc(100vw - ${sideMenuWidth} - ${workspaceWidth} - 64px)`;
  };

  return (
    <React.Fragment>
      <div
        style={{ width: progressWidth() }}
        className={classNames("progress-bar progress-bar__root", {
          "progress-position--bottom": position === "bottom",
          "is-expanded": !isExpandedView,
          "is-failed": progressItems && progressItems?.failCount > 0,
        })}
        id={"progressBar"}
        {...props}
      >
        <div className={"progress-bar__view"}>
          <div
            className={classNames("progress-bar__header", {
              oci: window.eco.serviceType === "OCI",
            })}
          >
            <Typography
              type="body2_rg"
              exactColor={getColor("COLOR_TEXT_INVERSE")}
              classes="mg mr-24"
              whiteSpace="nowrap"
            >
              {showStatusMsg()}
            </Typography>
            <div className={"progress-bar__header-total"}>
              <div
                style={{ width: drawProgressValue() }}
                className={"progress-bar__header-inner"}
              />
            </div>
            <div
              className={classNames("progress-bar__header-action", {
                isError: progressItems && progressItems?.failCount > 0,
              })}
            >
              <Tooltip
                message={t(
                  "file.progress." + (isExpandedView ? "shrink" : "expand")
                )}
              >
                <Button
                  onClick={() => setIsExpandedView(!isExpandedView)}
                  leadingIcon={
                    <Icons
                      variant="line"
                      label={isExpandedView ? "compress" : "expand"}
                      stroke={getColor("COLOR_ICON_INVERSE")}
                    />
                  }
                  btnStyle="icon"
                />
              </Tooltip>

              {isExpandedView &&
                progressItems &&
                progressItems?.totalCount ===
                  progressItems?.successCount + progressItems?.failCount && (
                  <Tooltip message={t("common.close")} classes={"mg ml-10"}>
                    <Button
                      onClick={() =>
                        setUpdateUploadProgressFiles({} as UploadFile)
                      }
                      leadingIcon={
                        <Icons
                          variant="line"
                          label="closeLarge"
                          stroke={getColor("COLOR_ICON_INVERSE")}
                        />
                      }
                      btnStyle="icon"
                    />
                  </Tooltip>
                )}
            </div>
          </div>
          <div
            className={classNames("progress-bar__body", {
              oci: window.eco.serviceType === "OCI",
            })}
          >
            {renderItems()}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ProgressBar;
